import { currentAuthToken } from "@homebound/auth-components";
import {
  BoundNumberField,
  Button,
  column,
  Css,
  GridColumn,
  Tooltip,
  GridDataRow,
  GridTable,
  RowStyles,
  useSnackbar,
  emptyCell,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { useCallback, useMemo, useState } from "react";
import { jsonToCSV } from "react-papaparse";
import { CsvUploader } from "src/components/CsvUploader";
import {
  LotInvestmentStatus,
  LotOverviewPageLotFragment,
  LotPartitionFragment,
  Maybe,
  SaveLotInput,
  SaveLotPartitionInput,
  useCreateDirectUnderwritingContractsMutation,
  useSaveLotMutation,
  useSaveLotPartitionsMutation,
  LotOverviewLotDocument,
} from "src/generated/graphql-types";
import { createImportCsvUrl } from "src/RouteUrls";
import { Percentage } from "src/components";
import { formatCentsToPrice } from "src/utils";
import { DateOnly } from "src/utils/dates";
import { BoundBeamDateField } from "src/components/BoundBeamDateField";

export type DirectUnderwritingLotPartitionsTableProps = {
  lotPartitions: LotPartitionFragment[];
  lot: LotOverviewPageLotFragment;
};

export function DirectUnderwritingLotPartitionsTable(props: DirectUnderwritingLotPartitionsTableProps) {
  const { lot, lotPartitions } = props;

  const isActiveInvestment = lot.lotInvestmentStatus.code === LotInvestmentStatus.Active;

  // Lot-level form state for underwriting fields
  const lotFormConfig: ObjectConfig<LotFormInput> = useMemo(
    () => ({
      id: { type: "value" },
      underwrittenSiteAcquisitionMarkupBasisPoints: { type: "value" },
      underwrittenPurchasePriceInCents: { type: "value" },
      underwrittenFinancingCostInCents: { type: "value" },
      acquisitionDate: { type: "value" },
      underContractAmountInCents: { type: "value" },
      purchasePriceInCents: { type: "value" },
    }),
    [],
  );

  const [saveLot] = useSaveLotMutation();

  const lotFormState = useFormState({
    config: lotFormConfig,
    init: {
      input: lot,
      map: mapLotToForm,
    },
    autoSave: async (formState) => {
      const { id, ...changedFields } = formState.changedValue;
      if (Object.keys(changedFields).length > 0) {
        await saveLot({
          variables: { input: { id: lot.id, ...changedFields } },
        });
      }
    },
    readOnly: isActiveInvestment,
  });

  const formConfig: ObjectConfig<FormInput> = useMemo(
    () => ({
      id: { type: "value" },
      lotPartitions: {
        type: "list",
        config: lotPartitionConfig,
      },
    }),
    [],
  );

  const formState = useFormState({
    config: formConfig,
    init: {
      input: lot,
      map: mapToForm,
    },
    autoSave: saveLotPartitions,
  });
  const [saveLotPartition] = useSaveLotPartitionsMutation();
  const [createDirectUnderwritingContracts] = useCreateDirectUnderwritingContractsMutation();

  async function saveLotPartitions(formState: ObjectState<FormInput>) {
    console.log("Saving Lot Partitions", formState.changedValue);
    const changedLotPartitions =
      formState.changedValue.lotPartitions?.filter(
        (lp) =>
          lp.directUnderwritingSoftCostsInCents !== undefined ||
          lp.directUnderwritingHardCostsInCents !== undefined ||
          lp.directUnderwritingHardCostMarkupBasisPoints !== undefined ||
          lp.directUnderwritingStartDate !== undefined ||
          lp.underwrittenSalesPriceInCents !== undefined ||
          lp.underwrittenSalesCommissionBasisPoints !== undefined ||
          lp.underwrittenLotOwnershipBasisPoints !== undefined ||
          lp.directUnderwritingContractPriceInCents !== undefined,
      ) || [];
    await Promise.all(
      changedLotPartitions.map(async (lp) => {
        // Form state stores ownership in basis points with decimals (e.g. 1234.5678 BP).
        // Backend wants ten-thousandths of basis points as Int (1 BP = 10,000 ten-thousandths-BP).
        const { underwrittenLotOwnershipBasisPoints, ...rest } = lp;
        const ownershipInput =
          underwrittenLotOwnershipBasisPoints != null
            ? {
                ...rest,
                underwrittenLotOwnershipTenThousandthsBasisPoints: Math.round(
                  underwrittenLotOwnershipBasisPoints * 10000,
                ),
              }
            : rest;
        // When underwrittenSalesPriceInCents changes, also update underwrittenSalesPriceWithoutAppreciationInCents to the same value
        const input =
          ownershipInput.underwrittenSalesPriceInCents !== undefined
            ? {
                ...ownershipInput,
                underwrittenSalesPriceWithoutAppreciationInCents: ownershipInput.underwrittenSalesPriceInCents,
              }
            : ownershipInput;
        return saveLotPartition({
          variables: { input: { lotPartitions: [input] } },
          refetchQueries: [{ query: LotOverviewLotDocument, variables: { lotId: lot.id } }],
        });
      }),
    );
  }

  const canBookDisabled = !lot.canContractDirectUnderwriting.allowed;
  const contractToBlueprintTooltip = canBookDisabled
    ? lot.canContractDirectUnderwriting.disabledReasons.map((reason) => reason.message).join("; ")
    : "Contract all outstanding Direct Underwriting to Blueprint";

  // "Make Active" is only allowed for a Pending investment where every lot partition has a direct
  // contract for each of its project stages, plus one (contracts.length === blueprintProjectStageCount + 1).
  // The extra contract accounts for the parent/top-level contract in Blueprint's project-stage hierarchy.
  const isPendingInvestment = lot.lotInvestmentStatus.code === LotInvestmentStatus.Pending;
  const partitionsMissingContracts = lotPartitions.filter(
    (lp) => lp.contracts.filter((c) => c.blueprintContractId).length !== lp.blueprintProjectStageCount + 1,
  );
  const allPartitionsContracted = partitionsMissingContracts.length === 0;
  // Every partition must have complete underwriting data before its lot-specific costs can be evaluated.
  const partitionsWithIncompleteData = lotPartitions.filter((lp) => !hasCompleteUnderwritingData(lp));
  const allPartitionsHaveCompleteData = partitionsWithIncompleteData.length === 0;
  // Every partition's currently-contracted amount in Blueprint must cover its underwritten lot-specific costs.
  const partitionsUnderContracted = lotPartitions.filter(
    (lp) => currentContractedUnderwrittenCostsInCents(lp) < lotSpecificUnderwrittenCostsInCents(lp),
  );
  const allPartitionsAdequatelyContracted = partitionsUnderContracted.length === 0;
  const makeActiveDisabledReason: string | false = !isPendingInvestment
    ? `This lot can only be made active from a Pending investment (current status: ${lot.lotInvestmentStatus.code}).`
    : !allPartitionsHaveCompleteData
      ? `Every lot partition must have complete underwriting data (direct hard costs, hard cost markup, and soft costs) before this lot can be made active. ` +
        `${partitionsWithIncompleteData.length} of ${lotPartitions.length} partition(s) are missing underwriting data.`
      : !allPartitionsContracted
        ? `Every lot partition must have a direct contract for each project stage before this lot can be made active. ` +
          `${partitionsMissingContracts.length} of ${lotPartitions.length} partition(s) still need contracts.`
        : !allPartitionsAdequatelyContracted
          ? `Every lot partition's current contracted underwritten costs must be at least its lot-specific underwritten costs before this lot can be made active. ` +
            `${partitionsUnderContracted.length} of ${lotPartitions.length} partition(s) are under-contracted.`
          : false;

  const { addError, clearErrors, csvContent, errors, loading, setCsvContent, upload } = useUploadDirectUnderwritingCsv(
    lot.id,
  );

  return (
    <>
      <div css={Css.mb2.p2.bgGray100.br8.$}>
        <div css={Css.smSb.mb1.$}>Lot-Level Underwriting</div>
        {isActiveInvestment && (
          <div css={Css.xsSb.gray600.mb2.$}>
            These fields cannot be edited because this lot is an active investment.
          </div>
        )}
        <div css={Css.df.gap2.$}>
          <div css={Css.w("200px").$}>
            <BoundNumberField
              label="Site Acquisition Markup %"
              type="basisPoints"
              field={lotFormState.underwrittenSiteAcquisitionMarkupBasisPoints}
            />
          </div>
          <div css={Css.w("200px").$}>
            <BoundNumberField
              label="Financing Cost"
              type="cents"
              field={lotFormState.underwrittenFinancingCostInCents}
            />
          </div>
          <div css={Css.w("200px").$}>
            <BoundBeamDateField label="Lot Acquisition Date" field={lotFormState.acquisitionDate} />
          </div>
          <div css={Css.w("250px").$}>
            <BoundNumberField
              label="Underwritten Purchase Price"
              type="cents"
              field={lotFormState.underwrittenPurchasePriceInCents}
              helperText={
                "The amount underwritten (Gross) for the purchase of this takedown - probably the same as Under Contract Amount (Gross)."
              }
            />
          </div>
          <div css={Css.w("250px").$}>
            <BoundNumberField
              label="Under Contract Amount (Gross)"
              type="cents"
              field={lotFormState.underContractAmountInCents}
            />
          </div>
          <div css={Css.w("200px").$}>
            <BoundNumberField
              label="Purchase Price (Net)"
              type="cents"
              field={lotFormState.purchasePriceInCents}
              helperText={"The Net Realized Amount paid"}
            />
          </div>
        </div>
      </div>

      <CsvUploader
        onError={addError}
        errors={errors}
        onDrop={(content) => {
          clearErrors();
          setCsvContent(
            jsonToCSV(
              content.map(({ data }) => data),
              { newline: "\n" }, // default is `\r\n` which breaks the parser on the backend
            ),
          );
        }}
      />
      <Button label="Import" disabled={!csvContent || loading} onClick={upload} />

      <GridTable
        id="underwritingLotPartitionsTable"
        rowStyles={rowStyles}
        columns={createPartitionColumns(formState, lotFormState)}
        rows={createPartitionRows(lotPartitions)}
        fallbackMessage="Partitions for this Lot will show here."
      />
      <Button
        variant="primary"
        disabled={canBookDisabled}
        tooltip={contractToBlueprintTooltip}
        onClick={async () => {
          await createDirectUnderwritingContracts({
            variables: {
              input: {
                lotId: lot.id,
              },
            },
          });
        }}
        label={"Confirm and Lock in Direct Underwriting to Blueprint"}
      />
      <Button
        variant="primary"
        disabled={makeActiveDisabledReason}
        onClick={async () => {
          await saveLot({
            variables: { input: { id: lot.id, lotInvestmentStatus: LotInvestmentStatus.Active } },
            refetchQueries: [{ query: LotOverviewLotDocument, variables: { lotId: lot.id } }],
          });
        }}
        label={"Make Active"}
      />
    </>
  );
}

/**
 * Whether a partition has the direct underwriting inputs needed to compute its lot-specific costs:
 * direct hard costs, the hard cost markup, and direct soft costs. A value of 0 is a legitimate entry
 * (e.g. a partition we've decided not to move forward with); only a blank/unset value is incomplete.
 */
function hasCompleteUnderwritingData(lp: LotPartitionFragment): boolean {
  return (
    lp.directUnderwritingHardCostsInCents != null &&
    lp.directUnderwritingHardCostMarkupBasisPoints != null &&
    lp.directUnderwritingSoftCostsInCents != null
  );
}

/**
 * The "Lot Specific Underwritten Costs Total" shown in the table: direct hard costs (with markup)
 * plus direct soft costs. Missing inputs are treated as 0 so the value is always comparable.
 */
function lotSpecificUnderwrittenCostsInCents(lp: LotPartitionFragment): number {
  const hardCosts = lp.directUnderwritingHardCostsInCents ?? 0;
  const markupBasisPoints = lp.directUnderwritingHardCostMarkupBasisPoints ?? 0;
  const softCosts = lp.directUnderwritingSoftCostsInCents ?? 0;
  return hardCosts * (markupBasisPoints / 10000) + hardCosts + softCosts;
}

/** The "Current Contracted Underwritten Costs Total" shown in the table. */
function currentContractedUnderwrittenCostsInCents(lp: LotPartitionFragment): number {
  return lp.underwrittenOpcoTotalCostInCents || 0;
}

type Row =
  | { kind: "header" }
  | { kind: "data"; data: LotPartitionFragment; id: string }
  | { kind: "totals"; data: TotalsData; id: string };

type TotalsData = {
  totalOwnershipBasisPoints: number;
  totalContractPriceInCents: number;
  totalSoftCostsInCents: number;
  totalHardCostsInCents: number;
  totalExitGrossSalePriceInCents: number;
};

const rowStyles: RowStyles<Row> = {
  header: {},
  data: {},
  totals: {},
};

function createPartitionRows(lotPartitions: LotPartitionFragment[]): GridDataRow<Row>[] {
  const dataRows: GridDataRow<Row>[] = [
    { kind: "header" as const, id: "header", data: undefined },
    ...lotPartitions.map((lp) => ({ kind: "data" as const, id: lp.id, data: lp })),
    {
      kind: "totals" as const,
      id: "totals",
      data: {
        totalOwnershipBasisPoints: lotPartitions.reduce(
          (sum, lp) => sum + (lp.underwrittenLotOwnershipTenThousandthsBasisPoints || 0) / 10000,
          0,
        ),
        totalContractPriceInCents: lotPartitions.reduce(
          (sum, lp) => sum + (lp.directUnderwritingContractPriceInCents || 0),
          0,
        ),
        totalSoftCostsInCents: lotPartitions.reduce((sum, lp) => sum + (lp.directUnderwritingSoftCostsInCents || 0), 0),
        totalHardCostsInCents: lotPartitions.reduce((sum, lp) => sum + (lp.directUnderwritingHardCostsInCents || 0), 0),
        totalExitGrossSalePriceInCents: lotPartitions.reduce(
          (sum, lp) => sum + (lp.underwrittenSalesPriceInCents || 0),
          0,
        ),
      },
    },
  ];
  return dataRows;
}

function createPartitionColumns(
  formState: ObjectState<FormInput>,
  lotFormState: ObjectState<LotFormInput>,
): GridColumn<Row>[] {
  const idColumn = column<Row>({
    header: () => "Partition ID",
    totals: () => "Totals",
    data: ({ id }) => id,
    w: "100px",
  });

  const blueprintIdColumn = column<Row>({
    header: () => "Blueprint Project ID",
    totals: () => emptyCell,
    data: ({ blueprintProjectId }) => blueprintProjectId,
    w: "100px",
  });

  const projectedStartDateColumn = column<Row>({
    header: () => "Projected Start Date",
    totals: () => emptyCell,
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.directUnderwritingStartDate,
        content: () => <BoundBeamDateField field={formStateField!.directUnderwritingStartDate} />,
      };
    },
    w: "150px",
  });

  const softCostColumn = column<Row>({
    header: () => "Direct Soft Costs",
    totals: ({ totalSoftCostsInCents }) => formatCentsToPrice(totalSoftCostsInCents),
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.directUnderwritingSoftCostsInCents,
        content: () => <BoundNumberField type="cents" field={formStateField!.directUnderwritingSoftCostsInCents} />,
        tooltip:
          "The total of underwritten soft costs for this property that are NOT included at the community/subdivision level",
      };
    },
    w: "200px",
  });

  const hardCostColumn = column<Row>({
    header: () => "Direct Hard Costs",
    totals: ({ totalHardCostsInCents }) => formatCentsToPrice(totalHardCostsInCents),
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.directUnderwritingHardCostsInCents,
        content: () => <BoundNumberField type="cents" field={formStateField!.directUnderwritingHardCostsInCents} />,
        tooltip:
          "The total of underwritten hard costs for this property that are NOT included at the community/subdivision level",
      };
    },
    w: "200px",
  });

  const hardCostMarkupColumn = column<Row>({
    header: () => "Hard Cost Markup %",
    totals: () => emptyCell,
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.directUnderwritingHardCostMarkupBasisPoints,
        content: () => (
          <BoundNumberField type="basisPoints" field={formStateField!.directUnderwritingHardCostMarkupBasisPoints} />
        ),
        tooltip: "The markup percentage for hard costs on this property",
      };
    },
    w: "200px",
  });

  const hardCostMarkupTotalColumn = column<Row>({
    header: () => "Hard Cost Markup Total",
    totals: () => emptyCell,
    data: ({ directUnderwritingHardCostMarkupBasisPoints, directUnderwritingHardCostsInCents }) => {
      if (!directUnderwritingHardCostMarkupBasisPoints || !directUnderwritingHardCostsInCents) {
        return undefined;
      }
      return formatCentsToPrice(
        directUnderwritingHardCostsInCents * (directUnderwritingHardCostMarkupBasisPoints / 10000),
      );
    },
    w: "100px",
  });

  const totalLotSpecificColumn = column<Row>({
    header: () => "Lot Specific Underwritten Costs Total",
    totals: () => emptyCell,
    data: (lp) => {
      if (!hasCompleteUnderwritingData(lp)) {
        return undefined;
      }
      return formatCentsToPrice(lotSpecificUnderwrittenCostsInCents(lp));
    },
    w: "200px",
  });

  const underwrittenContractedColumn = column<Row>({
    header: () => "Current Contracted Underwritten Costs Total",
    totals: () => emptyCell,
    data: (lp) => formatCentsToPrice(currentContractedUnderwrittenCostsInCents(lp)),
    w: "300px",
  });

  const underwrittenOwnershipPercentageColumn = column<Row>({
    header: () => "UW Ownership %",
    totals: ({ totalOwnershipBasisPoints }) => {
      const isOver100 = totalOwnershipBasisPoints > 10000;
      return {
        content: () => (
          <span css={isOver100 ? Css.red600.$ : undefined}>
            <Percentage percent={totalOwnershipBasisPoints / 100} />
          </span>
        ),
      };
    },
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.underwrittenLotOwnershipTenThousandthsBasisPoints,
        content: () => (
          <BoundNumberField
            type="basisPoints"
            numFractionDigits={3}
            field={formStateField!.underwrittenLotOwnershipBasisPoints}
          />
        ),
      };
    },
    w: "130px",
  });

  const underwrittenOwnershipAmountColumn = column<Row>({
    header: () => "Gross Lot Acq Cost",
    totals: ({ totalContractPriceInCents }) => {
      const underContractAmount = lotFormState.underContractAmountInCents.value;
      const isOver = underContractAmount != null && totalContractPriceInCents > underContractAmount;
      return {
        content: () => (
          <span css={isOver ? Css.red600.$ : undefined}>{formatCentsToPrice(totalContractPriceInCents)}</span>
        ),
      };
    },
    data: ({ id, canEditDirectUnderwritingContractPriceInCents, directUnderwritingContractPriceInCents }) => {
      if (!canEditDirectUnderwritingContractPriceInCents.allowed) {
        const tooltip = canEditDirectUnderwritingContractPriceInCents.disabledReasons.map((r) => r.message).join("; ");
        return <Tooltip title={tooltip}>{formatCentsToPrice(directUnderwritingContractPriceInCents || 0)}</Tooltip>;
      }
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === id);
      return {
        value: () => directUnderwritingContractPriceInCents,
        content: () => <BoundNumberField type="cents" field={formStateField!.directUnderwritingContractPriceInCents} />,
      };
    },
    w: "200px",
  });

  const exitGrossPriceColumn = column<Row>({
    header: () => "Exit Gross Sale Price",
    totals: ({ totalExitGrossSalePriceInCents }) => formatCentsToPrice(totalExitGrossSalePriceInCents),
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.underwrittenSalesPriceInCents,
        content: () => <BoundNumberField type="cents" field={formStateField!.underwrittenSalesPriceInCents} />,
        tooltip: "The underwritten gross sales price for this property",
      };
    },
    w: "200px",
  });

  const exitCommissionRateColumn = column<Row>({
    header: () => "Exit Commission %",
    totals: () => emptyCell,
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.underwrittenSalesCommissionBasisPoints,
        content: () => (
          <BoundNumberField type="basisPoints" field={formStateField!.underwrittenSalesCommissionBasisPoints} />
        ),
        tooltip: "The underwritten percentage for exit commission on this property",
      };
    },
    w: "200px",
  });

  const exitNetPriceColumn = column<Row>({
    header: () => "Exit Net Sale Price",
    totals: () => emptyCell,
    data: ({ underwrittenSalesPriceInCents, underwrittenSalesCommissionBasisPoints }) => {
      if (!underwrittenSalesPriceInCents) {
        return undefined;
      }
      return formatCentsToPrice(underwrittenSalesPriceInCents * (1 - underwrittenSalesCommissionBasisPoints / 10000));
    },
    w: "200px",
  });

  return [
    idColumn,
    blueprintIdColumn,
    projectedStartDateColumn,
    softCostColumn,
    hardCostColumn,
    hardCostMarkupColumn,
    hardCostMarkupTotalColumn,
    totalLotSpecificColumn,
    underwrittenContractedColumn,
    underwrittenOwnershipPercentageColumn,
    underwrittenOwnershipAmountColumn,
    exitGrossPriceColumn,
    exitCommissionRateColumn,
    exitNetPriceColumn,
  ];
}

type FormInput = SaveLotPartitionInput & {
  lotPartitions: Maybe<SaveLotPartitionInput[]>;
};

type LotFormInput = Pick<
  SaveLotInput,
  | "id"
  | "underwrittenSiteAcquisitionMarkupBasisPoints"
  | "underwrittenPurchasePriceInCents"
  | "underwrittenFinancingCostInCents"
  | "underContractAmountInCents"
  | "purchasePriceInCents"
> & {
  acquisitionDate: DateOnly | null | undefined;
};

function mapLotToForm(lot: LotOverviewPageLotFragment): LotFormInput {
  return {
    id: lot.id,
    underwrittenSiteAcquisitionMarkupBasisPoints: lot.underwrittenSiteAcquisitionMarkupBasisPoints,
    underwrittenPurchasePriceInCents: lot.underwrittenPurchasePriceInCents,
    underwrittenFinancingCostInCents: lot.underwrittenFinancingCostInCents,
    acquisitionDate: lot.acquisitionDate ? new DateOnly(lot.acquisitionDate) : undefined,
    underContractAmountInCents: lot.underContractAmountInCents,
    purchasePriceInCents: lot.purchasePriceInCents,
  };
}

function mapToForm(lot: LotOverviewPageLotFragment): FormInput {
  return {
    id: lot.id,
    lotPartitions: mapLotPartitionsToForm(lot.partitions),
  };
}

export function mapLotPartitionsToForm(lotPartitions: Maybe<LotPartitionFragment[]>) {
  return lotPartitions?.map((lp) => {
    return {
      id: lp.id,
      directUnderwritingSoftCostsInCents: lp.directUnderwritingSoftCostsInCents,
      directUnderwritingHardCostsInCents: lp.directUnderwritingHardCostsInCents,
      directUnderwritingHardCostMarkupBasisPoints: lp.directUnderwritingHardCostMarkupBasisPoints,
      directUnderwritingStartDate: lp.directUnderwritingStartDate
        ? new DateOnly(lp.directUnderwritingStartDate)
        : undefined,
      underwrittenSalesPriceInCents: lp.underwrittenSalesPriceInCents,
      underwrittenSalesCommissionBasisPoints: lp.underwrittenSalesCommissionBasisPoints,
      underwrittenLotOwnershipBasisPoints: lp.underwrittenLotOwnershipTenThousandthsBasisPoints / 10000,
      directUnderwritingContractPriceInCents: lp.directUnderwritingContractPriceInCents,
    };
  });
}

export const lotPartitionConfig: ObjectConfig<SaveLotPartitionInput> = {
  id: { type: "value" },
  directUnderwritingSoftCostsInCents: { type: "value" },
  directUnderwritingHardCostsInCents: { type: "value" },
  directUnderwritingHardCostMarkupBasisPoints: { type: "value" },
  directUnderwritingStartDate: { type: "value" },
  underwrittenSalesPriceInCents: { type: "value" },
  underwrittenSalesCommissionBasisPoints: { type: "value" },
  underwrittenLotOwnershipBasisPoints: { type: "value" },
  directUnderwritingContractPriceInCents: { type: "value" },
};

function useUploadDirectUnderwritingCsv(lotId: string) {
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [csvContent, setCsvContent] = useState("");
  const { triggerNotice } = useSnackbar();

  const addError = (err: unknown) => setErrors((existing) => existing.concat(String(err)));
  const clearErrors = () => setErrors([]);

  const upload = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    const response = await fetch(`${createImportCsvUrl("directUnderwritingUpload")}&lotId=${lotId}`, {
      method: "POST",
      headers: { "content-type": "text/csv", Authorization: `Bearer ${await currentAuthToken()}` },
      body: csvContent,
    });

    if (response.status !== 200) {
      // Backend may have identified errors in the CSV (Like unfilled columns or poorly-formatted numbers)
      // so pop them into the Errors array here.
      const { message } = await response.json();
      if (message) addError(message);
      setLoading(false);
      return;
    }

    triggerNotice({ message: "Direct Underwriting Information Uploaded Successfully" });
    // Just reload the page to see the new data - come back to this later and do a better job of updating the UI
    window.location.reload();
  }, [loading, lotId, csvContent, triggerNotice]);

  return { addError, clearErrors, csvContent, errors, loading, setCsvContent, upload };
}
